/**
 * useGmail Hook — Gmail Integration mit Modul-Level-Cache
 * 
 * ARCHITEKTUR:
 * - Modul-Level-Cache: E-Mails überleben Tab-Wechsel (kein Neuladen)
 * - PAGE_SIZE = 50: Nur 50 E-Mails initial laden (schnell, kein Browser-Crash)
 * - Hintergrund-Polling: Alle 30 Sek prüft auf neue E-Mails (1 API-Call)
 * - Sofort-Sync: Bei jedem Seitenaufruf und Tab-Fokus wird sofort synchronisiert
 * - Kein automatischer Massen-Sync: Nur manuell über Einstellungen
 * - Body on-demand: HTML-Body nur bei Klick laden
 */

import { useState, useEffect, useCallback, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { cache, getCache, subscribe, updateCache } from './emailCache';
export type { EmailCache } from './emailCache';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '967597285093-v79e3dq5hlfi6d0q1fp1fbuk01dbns98.apps.googleusercontent.com';
const REDIRECT_URI = `${window.location.origin}/auth/google/callback`;

const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/gmail.modify',
  'https://www.googleapis.com/auth/userinfo.email',
].join(' ');

// Konfiguration
const PAGE_SIZE = 50; // Nur 50 E-Mails pro Seite — schnell und stabil
const POLLING_INTERVAL = 15_000; // 15 Sekunden — Echtzeit-Sync
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const DB_TIMEOUT = 8000; // 8s Timeout für DB-Operationen

function getGmailSettings() {
  try {
    const stored = localStorage.getItem('gmail_settings');
    if (stored) {
      const settings = JSON.parse(stored);
      // IMMER den aktuellen POLLING_INTERVAL verwenden (nicht den alten gespeicherten)
      settings.pollingInterval = POLLING_INTERVAL;
      return settings;
    }
  } catch { /* ignore */ }
  return { pollingInterval: POLLING_INTERVAL };
}

export interface GmailMessage {
  id: string;
  threadId: string;
  snippet: string;
  labelIds: string[];
  from: string;
  to: string;
  cc?: string;
  subject: string;
  date: string;
  isUnread: boolean;
  body?: string;
  bodyHtml?: string;
  attachments?: { filename: string; mimeType: string; attachmentId: string; size: number }[];
}

export interface GmailLabel {
  id: string;
  name: string;
  type: string;
  messagesTotal?: number;
  messagesUnread?: number;
}

export interface GmailLoadProgress {
  phase: 'connecting' | 'listing' | 'fetching' | 'syncing' | 'done' | 'error';
  loaded: number;
  total: number;
  message: string;
}

function getUserId(): string {
  let userId = localStorage.getItem('gmail_user_id');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem('gmail_user_id', userId);
  }
  return userId;
}

async function withRetry<T>(fn: () => Promise<T>, retries: number = MAX_RETRIES): Promise<T> {
  let lastError: any;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, attempt)));
      }
    }
  }
  throw lastError;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string = 'Operation'): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} Timeout nach ${ms}ms`)), ms);
    promise.then(
      (val) => { clearTimeout(timer); resolve(val); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}

async function callGmailApi(action: string, params: Record<string, any> = {}) {
  return withRetry(async () => {
    const userId = getUserId();
    const response = await fetch('/api/gmail-api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, userId, ...params }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Gmail API Error (${response.status}): ${errorText}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      if (data.error?.code === 401 || data.error === 'Not authenticated') {
        await callGmailAuth('refresh', { user_id: userId });
        const retryResponse = await fetch('/api/gmail-api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, userId, ...params }),
        });
        return await retryResponse.json();
      }
      throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
    }
    
    return data;
  });
}

async function callGmailAuth(action: string, params: Record<string, any> = {}) {
  const response = await fetch('/api/gmail-auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  return await response.json();
}

function decodeBase64Utf8(base64: string): string {
  try {
    const standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = standardBase64 + '='.repeat((4 - standardBase64.length % 4) % 4);
    try {
      const binaryString = atob(padded);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch {
      return atob(padded);
    }
  } catch {
    return base64;
  }
}

function dbRecordToGmailMessage(record: any): GmailMessage {
  return {
    id: record.gmail_message_id,
    threadId: record.thread_id || '',
    snippet: record.snippet || '',
    labelIds: record.label_ids || [],
    from: record.from_name ? `${record.from_name} <${record.from_email}>` : (record.from_email || ''),
    to: (record.to_emails || []).join(', '),
    cc: (record.cc_emails || []).join(', '),
    subject: record.subject || '',
    date: record.date || new Date(record.internal_date).toISOString(),
    isUnread: !record.is_read,
    body: record.body_text ? decodeBase64Utf8(record.body_text) : undefined,
    bodyHtml: record.body_html ? decodeBase64Utf8(record.body_html) : undefined,
    attachments: record.attachments || [],
  };
}

function parseMessageHeaders(msg: any): GmailMessage {
  const headers = msg.payload?.headers || [];
  const getHeader = (name: string) => headers.find((h: any) => h.name.toLowerCase() === name.toLowerCase())?.value || '';
  
  let body = '';
  let bodyHtml = '';
  const attachments: NonNullable<GmailMessage['attachments']> = [];

  function extractParts(payload: any) {
    if (!payload) return;
    if (payload.mimeType === 'text/plain' && payload.body?.data) {
      body = decodeBase64Utf8(payload.body.data);
    }
    if (payload.mimeType === 'text/html' && payload.body?.data) {
      bodyHtml = decodeBase64Utf8(payload.body.data);
    }
    if (payload.filename && payload.body?.attachmentId) {
      attachments.push({
        filename: payload.filename,
        mimeType: payload.mimeType,
        attachmentId: payload.body.attachmentId,
        size: payload.body.size || 0,
      });
    }
    if (payload.parts) payload.parts.forEach(extractParts);
  }
  extractParts(msg.payload);

  return {
    id: msg.id,
    threadId: msg.threadId,
    snippet: msg.snippet || '',
    labelIds: msg.labelIds || [],
    from: getHeader('From'),
    to: getHeader('To'),
    cc: getHeader('Cc'),
    subject: getHeader('Subject'),
    date: getHeader('Date'),
    isUnread: (msg.labelIds || []).includes('UNREAD'),
    body,
    bodyHtml,
    attachments,
  };
}

export interface EmailAttachment {
  filename: string;
  mimeType: string;
  base64Data: string; // base64-encoded file content
}

function createRawEmail(to: string, subject: string, body: string, cc?: string, bcc?: string, inReplyTo?: string, references?: string, fromEmail?: string, attachments?: EmailAttachment[]): string {
  const boundary = '----=_Part_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
  const hasAttachments = attachments && attachments.length > 0;

  let email = '';
  if (fromEmail) email += `From: ${fromEmail}\r\n`;
  email += `To: ${to}\r\n`;
  if (cc) email += `Cc: ${cc}\r\n`;
  if (bcc) email += `Bcc: ${bcc}\r\n`;
  email += `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=\r\n`;
  email += `MIME-Version: 1.0\r\n`;
  if (inReplyTo) {
    email += `In-Reply-To: ${inReplyTo}\r\n`;
    email += `References: ${references || inReplyTo}\r\n`;
  }

  if (hasAttachments) {
    // Multipart/mixed für E-Mail mit Anhängen
    email += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n`;
    email += `\r\n`;
    // HTML-Body-Part
    email += `--${boundary}\r\n`;
    email += `Content-Type: text/html; charset=UTF-8\r\n`;
    email += `Content-Transfer-Encoding: base64\r\n`;
    email += `\r\n`;
    email += btoa(unescape(encodeURIComponent(body)));
    email += `\r\n`;
    // Anhänge
    for (const att of attachments!) {
      email += `--${boundary}\r\n`;
      email += `Content-Type: ${att.mimeType}; name="${att.filename}"\r\n`;
      email += `Content-Disposition: attachment; filename="${att.filename}"\r\n`;
      email += `Content-Transfer-Encoding: base64\r\n`;
      email += `\r\n`;
      email += att.base64Data;
      email += `\r\n`;
    }
    email += `--${boundary}--\r\n`;
  } else {
    // Einfache HTML-E-Mail ohne Anhänge
    email += `Content-Type: text/html; charset=UTF-8\r\n`;
    email += `Content-Transfer-Encoding: base64\r\n`;
    email += `\r\n`;
    email += btoa(unescape(encodeURIComponent(body)));
  }

  return btoa(unescape(encodeURIComponent(email)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// ============================================================
// MODUL-LEVEL-CACHE — überlebt Tab-Wechsel (ausgelagert in emailCache.ts)
// ============================================================

let initialized = false;
let fetchInProgress = false;
let pollingTimer: ReturnType<typeof setInterval> | null = null;
let syncInProgress = false;

// ============================================================
// MODUL-LEVEL SYNC — kann von fetchMessages aufgerufen werden
// ============================================================
let lastPollTime = 0;
const MIN_POLL_GAP = 5000; // Mindestens 5 Sek zwischen Polls

async function pollForNewMessagesInternal() {
  const now = Date.now();
  if (now - lastPollTime < MIN_POLL_GAP) return; // Zu früh — überspringen
  if (!cache.isConnected || cache.isPolling || syncInProgress) return;
  lastPollTime = now;
  updateCache({ isPolling: true });
  
  try {
    const folder = cache.currentLabel === 'ALL' ? 'ALL' : cache.currentLabel;

    // PARALLEL: historySync + getEmailsFromDb gleichzeitig starten
    // historySync schreibt neue E-Mails in die DB
    // getEmailsFromDb lädt die neuesten E-Mails aus der DB
    // Beim nächsten Poll-Zyklus sind die neuen E-Mails dann in der DB
    const [, refreshResult, unreadResult] = await Promise.allSettled([
      callGmailApi('historySync', {}).catch(() => null),
      callGmailApi('getEmailsFromDb', { folder, limit: PAGE_SIZE }).catch(() => null),
      callGmailApi('listMessages', { labelIds: ['INBOX', 'UNREAD'], maxResults: 1 }).catch(() => null),
    ]);

    // DB-Ergebnis verarbeiten
    if (refreshResult.status === 'fulfilled' && refreshResult.value?.emails?.length > 0) {
      const data = refreshResult.value;
      const newFirstId = data.emails[0]?.gmail_message_id;
      const oldFirstId = cache.messages[0]?.id;
      if (newFirstId !== oldFirstId || data.totalCount !== cache.totalCount) {
        updateCache({
          messages: data.emails.map(dbRecordToGmailMessage),
          hasMore: data.hasMore || false,
          totalCount: data.totalCount || data.emails.length,
          lastSyncTime: new Date().toISOString(),
        });
      }
    }

    // Unread Count aktualisieren
    if (unreadResult.status === 'fulfilled' && unreadResult.value?.resultSizeEstimate !== undefined) {
      updateCache({ unreadCount: unreadResult.value.resultSizeEstimate || 0 });
    }

  } catch (err) {
    console.warn('[Gmail Sofort-Sync] Fehler:', err);
  } finally {
    updateCache({ isPolling: false });
  }
}

// ============================================================
// HOOK
// ============================================================
export function useGmail() {
  const state = useSyncExternalStore(subscribe, getCache, getCache);
  
  const [pollingInterval, setPollingIntervalState] = useState(getGmailSettings().pollingInterval);
  const searchQueryRef = useRef<string | undefined>(undefined);
  const currentLabelRef = useRef('INBOX');

  useEffect(() => {
    currentLabelRef.current = state.currentLabel;
  }, [state.currentLabel]);

  // ─── Check connection on mount ─────────────────────────────────────
  const checkConnection = useCallback(async () => {
    if (initialized && cache.isConnected && !cache.needsReconnect) {
      // Schon verbunden und Cache hat Daten → nichts tun
      updateCache({ loading: false });
      return;
    }
    
    updateCache({ loadProgress: { phase: 'connecting', loaded: 0, total: 0, message: 'Verbinde mit Gmail...' } });
    try {
      const userId = getUserId();
      const result = await callGmailAuth('status', { user_id: userId });
      
      // Token ist revoked/abgelaufen → User muss sich neu verbinden
      if (result.needs_reconnect) {
        updateCache({ 
          isConnected: false, 
          needsReconnect: true,
          connectedEmail: result.google_email || null, 
          lastError: result.error || 'Gmail-Zugriff abgelaufen. Bitte erneut verbinden.',
          loading: false,
        });
        return;
      }
      
      if (result.connected) {
        updateCache({ 
          isConnected: true, 
          needsReconnect: false,
          connectedEmail: result.google_email || null, 
          lastError: null,
          loading: false,
        });
        try {
          const inbox = await callGmailApi('listMessages', { labelIds: ['INBOX', 'UNREAD'], maxResults: 1 });
          if (inbox.resultSizeEstimate !== undefined) {
            updateCache({ unreadCount: inbox.resultSizeEstimate || 0 });
          }
        } catch { /* ignore */ }
      } else {
        updateCache({ isConnected: false, needsReconnect: false, loading: false });
        if (result.error) updateCache({ lastError: result.error });
      }
    } catch (err: any) {
      updateCache({ isConnected: false, loading: false, lastError: err.message || 'Verbindungsfehler' });
    }
  }, []);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // ─── Connect Gmail (OAuth) ─────────────────────────────────────────
  const connect = useCallback((returnPath: string = '/kommunikation') => {
    localStorage.setItem('gmail_return_path', returnPath);
    const userId = getUserId();
    localStorage.setItem('gmail_oauth_state', userId);
    const stateValue = `gmail_${userId}`;
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GMAIL_SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', stateValue);
    window.location.href = authUrl.toString();
  }, []);

  // ─── Fetch messages (DB-First, mit Cache + Sofort-Sync) ───────────
  const fetchMessages = useCallback(async (labelId: string = 'INBOX', query?: string) => {
    // Wenn Cache bereits Daten hat UND gleicher Label → sofort anzeigen, ABER trotzdem Hintergrund-Sync starten
    if (initialized && cache.messages.length > 0 && cache.currentLabel === labelId && !query && !fetchInProgress) {
      updateCache({ loading: false, loadingMessages: false });
      // SOFORT-SYNC: Im Hintergrund auf neue E-Mails prüfen (nicht blockierend)
      if (!syncInProgress) {
        pollForNewMessagesInternal();
      }
      return;
    }
    
    if (fetchInProgress) return;
    fetchInProgress = true;
    
    updateCache({ loadingMessages: true, lastError: null });
    searchQueryRef.current = query;

    const folderMap: Record<string, string> = {
      'INBOX': 'INBOX', 'SENT': 'SENT', 'TRASH': 'TRASH',
      'STARRED': 'STARRED', 'UNREAD': 'UNREAD', 'ALL': 'ALL',
    };
    const folder = folderMap[labelId] || 'ALL';

    try {
      // DB-First: Nur PAGE_SIZE E-Mails laden (schnell!)
      updateCache({ loadProgress: { phase: 'connecting', loaded: 0, total: 0, message: 'Lade E-Mails...' } });
      
      const dbResult = await withTimeout(
        callGmailApi('getEmailsFromDb', { 
          folder, limit: PAGE_SIZE, offset: 0, search: query || undefined,
        }),
        DB_TIMEOUT,
        'DB-Abfrage'
      );

      if (dbResult.emails && dbResult.emails.length > 0) {
        const parsed = dbResult.emails.map(dbRecordToGmailMessage);
        updateCache({
          messages: parsed,
          currentLabel: labelId,
          hasMore: dbResult.hasMore || false,
          totalCount: dbResult.totalCount || parsed.length,
          loadProgress: { 
            phase: 'done', loaded: parsed.length, 
            total: dbResult.totalCount || parsed.length, 
            message: `${parsed.length} von ${dbResult.totalCount || parsed.length} E-Mails` 
          },
          lastSyncTime: new Date().toISOString(),
        });
        initialized = true;

        // History-Sync im Hintergrund (nicht blockierend, kein Reload)
        callGmailApi('historySync', {}).then(historyResult => {
          if (historyResult.newEmails && historyResult.newEmails > 0) {
            toast.info(`${historyResult.newEmails} neue E-Mail(s) synchronisiert`);
            // Nur die neuesten PAGE_SIZE E-Mails neu laden
            callGmailApi('getEmailsFromDb', { folder, limit: PAGE_SIZE, search: query || undefined })
              .then(refreshResult => {
                if (refreshResult.emails && refreshResult.emails.length > 0) {
                  updateCache({
                    messages: refreshResult.emails.map(dbRecordToGmailMessage),
                    hasMore: refreshResult.hasMore || false,
                    totalCount: refreshResult.totalCount || refreshResult.emails.length,
                    loadProgress: { 
                      phase: 'done', loaded: refreshResult.emails.length,
                      total: refreshResult.totalCount || refreshResult.emails.length,
                      message: `${refreshResult.emails.length} E-Mails (aktualisiert)` 
                    },
                  });
                }
              })
              .catch(() => {});
          }
        }).catch(() => {});

      } else {
        // DB leer → Gmail API Fallback (nur PAGE_SIZE laden!)
        console.log('[Gmail] DB leer, lade von Gmail API...');
        updateCache({ loadProgress: { phase: 'listing', loaded: 0, total: 0, message: 'Lade von Gmail...' } });
        
        const listResult = await callGmailApi('listAllMessages', { 
          labelIds: labelId === 'ALL' ? [] : [labelId], 
          q: query, maxTotal: PAGE_SIZE 
        });
        const messageIds = (listResult.messages || []).map((m: any) => m.id);
        
        if (messageIds.length > 0) {
          const batchResult = await callGmailApi('batchGetMessages', { messageIds, format: 'metadata' });
          if (batchResult.messages) {
            const parsed = batchResult.messages
              .filter((m: any) => m && m.id && !m.error)
              .map(parseMessageHeaders)
              .sort((a: GmailMessage, b: GmailMessage) => new Date(b.date).getTime() - new Date(a.date).getTime());
            updateCache({
              messages: parsed,
              currentLabel: labelId,
              hasMore: true, // Gmail hat sicher mehr
              totalCount: parsed.length,
              loadProgress: { phase: 'done', loaded: parsed.length, total: parsed.length, message: `${parsed.length} E-Mails geladen` },
              lastSyncTime: new Date().toISOString(),
            });
            initialized = true;
          }
        } else {
          updateCache({
            messages: [],
            currentLabel: labelId,
            loadProgress: { phase: 'done', loaded: 0, total: 0, message: 'Keine E-Mails' },
          });
          initialized = true;
        }

        // Hintergrund-Sync starten (nicht blockierend)
        if (!syncInProgress) {
          runInitialSyncInternal().catch(() => {});
        }
      }

    } catch (err: any) {
      console.error('Failed to fetch messages:', err);
      updateCache({ 
        lastError: err.message || 'Fehler beim Laden',
        loadProgress: { phase: 'error', loaded: 0, total: 0, message: err.message || 'Fehler' },
      });
    } finally {
      updateCache({ loadingMessages: false });
      fetchInProgress = false;
    }
  }, []);

  // ─── Load more messages (Infinite Scroll) ──────────────────────────
  const loadMoreMessages = useCallback(async () => {
    if (fetchInProgress || !cache.hasMore) return;
    fetchInProgress = true;
    updateCache({ loadingMessages: true });
    
    try {
      const folder = currentLabelRef.current === 'ALL' ? 'ALL' : currentLabelRef.current;
      const offset = cache.messages.length;
      
      const dbResult = await withTimeout(
        callGmailApi('getEmailsFromDb', { 
          folder, limit: PAGE_SIZE, offset, search: searchQueryRef.current || undefined,
        }),
        DB_TIMEOUT,
        'DB-LoadMore'
      );
      
      if (dbResult.emails && dbResult.emails.length > 0) {
        const newMessages = dbResult.emails.map(dbRecordToGmailMessage);
        const combined = [...cache.messages, ...newMessages];
        updateCache({
          messages: combined,
          hasMore: dbResult.hasMore || false,
          totalCount: dbResult.totalCount || combined.length,
          loadProgress: { 
            phase: 'done', loaded: combined.length,
            total: dbResult.totalCount || combined.length,
            message: `${combined.length} von ${dbResult.totalCount || combined.length} E-Mails` 
          },
        });
      } else {
        updateCache({ hasMore: false });
      }
    } catch (err: any) {
      console.error('Load more failed:', err);
    } finally {
      updateCache({ loadingMessages: false });
      fetchInProgress = false;
    }
  }, []);

  // ─── Polling: Prüfe auf neue E-Mails (delegiert an Modul-Level-Funktion) ───
  const pollForNewMessages = useCallback(async () => {
    await pollForNewMessagesInternal();
  }, []);

  // Polling starten/stoppen — EINMAL global
  useEffect(() => {
    if (!state.isConnected) return;

    const startPolling = () => {
      if (pollingTimer) clearInterval(pollingTimer);
      pollingTimer = setInterval(pollForNewMessages, pollingInterval);
    };

    const stopPolling = () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
        pollingTimer = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        pollForNewMessages();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isConnected, pollForNewMessages, pollingInterval]);

  // ─── Initial Sync (nur manuell oder bei leerer DB) ─────────────────
  const runInitialSyncInternal = async () => {
    if (syncInProgress) return;
    syncInProgress = true;
    
    try {
      let totalSynced = 0;
      let needsMore = true;
      let iteration = 0;
      const maxIterations = 50;
      
      while (needsMore && iteration < maxIterations) {
        iteration++;
        const result = await callGmailApi('syncEmails', { maxTotal: 50000, batchSize: 500 });
        
        if (result.error) {
          console.error('Sync error:', result.error);
          break;
        }
        
        totalSynced += result.synced || 0;
        needsMore = result.needsMore || false;
        
        // Alle 5 Iterationen: Aktualisiere die Anzeige
        if (totalSynced > 0 && iteration % 5 === 0) {
          try {
            const folder = currentLabelRef.current === 'ALL' ? 'ALL' : currentLabelRef.current;
            const dbResult = await callGmailApi('getEmailsFromDb', { folder, limit: PAGE_SIZE });
            if (dbResult.emails && dbResult.emails.length > 0) {
              updateCache({
                messages: dbResult.emails.map(dbRecordToGmailMessage),
                hasMore: dbResult.hasMore || false,
                totalCount: dbResult.totalCount || dbResult.emails.length,
              });
            }
          } catch { /* ignore */ }
        }
      }
      
      if (totalSynced > 0) {
        toast.success(`${totalSynced} E-Mails synchronisiert`);
        try {
          const folder = currentLabelRef.current === 'ALL' ? 'ALL' : currentLabelRef.current;
          const dbResult = await callGmailApi('getEmailsFromDb', { folder, limit: PAGE_SIZE });
          if (dbResult.emails && dbResult.emails.length > 0) {
            updateCache({
              messages: dbResult.emails.map(dbRecordToGmailMessage),
              hasMore: dbResult.hasMore || false,
              totalCount: dbResult.totalCount || dbResult.emails.length,
              loadProgress: { 
                phase: 'done', loaded: dbResult.emails.length,
                total: dbResult.totalCount || dbResult.emails.length,
                message: `${dbResult.emails.length} E-Mails geladen (synchronisiert)` 
              },
            });
          }
        } catch { /* ignore */ }
      }
    } catch (err: any) {
      console.error('Initial sync failed:', err);
    } finally {
      syncInProgress = false;
    }
  };

  // ─── Single message (on-demand body) ───────────────────────────────
  const getMessage = useCallback(async (messageId: string): Promise<GmailMessage | null> => {
    try {
      try {
        const dbResult = await withTimeout(
          callGmailApi('getEmailFromDb', { messageId }),
          3000, 'DB getMessage'
        );
        if (dbResult.email && (dbResult.email.body_html || dbResult.email.body_text)) {
          return dbRecordToGmailMessage(dbResult.email);
        }
      } catch { /* DB fehlgeschlagen → Gmail API */ }
      
      const result = await callGmailApi('getMessage', { messageId, format: 'full' });
      if (result.id) return parseMessageHeaders(result);
      return null;
    } catch (err: any) {
      console.error('Failed to get message:', err);
      updateCache({ lastError: err.message || 'Fehler beim Laden der E-Mail' });
      return null;
    }
  }, []);

  // Thread laden
  const getThread = useCallback(async (threadId: string, fromEmail?: string): Promise<GmailMessage[]> => {
    try {
      const result = await callGmailApi('getThread', { threadId, fromEmail });
      if (result.messages && result.messages.length > 0) {
        return result.messages.map(parseMessageHeaders);
      }
      // Fallback: If no messages returned (permission issue), try without fromEmail
      if (!fromEmail) return [];
      const fallback = await callGmailApi('getThread', { threadId });
      if (fallback.messages && fallback.messages.length > 0) {
        return fallback.messages.map(parseMessageHeaders);
      }
      return [];
    } catch (err: any) {
      console.error('Failed to get thread:', err);
      updateCache({ lastError: err.message || 'Fehler beim Laden des Threads' });
      return [];
    }
  }, []);

  // Send email (mit optionalen Anhängen)
  const sendEmail = useCallback(async (to: string, subject: string, body: string, cc?: string, bcc?: string, inReplyTo?: string, references?: string, attachments?: EmailAttachment[], fromEmail?: string) => {
    const senderEmail = fromEmail || cache.connectedEmail || undefined;
    const raw = createRawEmail(to, subject, body, cc, bcc, inReplyTo, references, senderEmail, attachments);
    const result = await callGmailApi('sendMessage', { raw, fromEmail: senderEmail });
    setTimeout(() => pollForNewMessages(), 2000);
    return result;
  }, [pollForNewMessages]);

  // Mark as read/unread
  const markAsRead = useCallback(async (messageId: string) => {
    const result = await callGmailApi('modifyMessage', { messageId, removeLabelIds: ['UNREAD'] });
    updateCache(prev => ({
      messages: prev.messages.map(m => m.id === messageId ? { ...m, isUnread: false, labelIds: m.labelIds.filter(l => l !== 'UNREAD') } : m),
      unreadCount: Math.max(0, prev.unreadCount - 1),
    }));
    return result;
  }, []);

  const markAsUnread = useCallback(async (messageId: string) => {
    const result = await callGmailApi('modifyMessage', { messageId, addLabelIds: ['UNREAD'] });
    updateCache(prev => ({
      messages: prev.messages.map(m => m.id === messageId ? { ...m, isUnread: true, labelIds: [...m.labelIds, 'UNREAD'] } : m),
      unreadCount: prev.unreadCount + 1,
    }));
    return result;
  }, []);

  // Trash/Archive/Star — funktionaler Updater verhindert Stale-Closure-Bug (#83)
  const trashMessage = useCallback(async (messageId: string) => {
    const result = await callGmailApi('trashMessage', { messageId });
    updateCache(prev => ({ messages: prev.messages.filter(m => m.id !== messageId) }));
    return result;
  }, []);

  const archiveMessage = useCallback(async (messageId: string) => {
    const result = await callGmailApi('modifyMessage', { messageId, removeLabelIds: ['INBOX'] });
    updateCache(prev => ({ messages: prev.messages.filter(m => m.id !== messageId) }));
    return result;
  }, []);

  const reportSpam = useCallback(async (messageId: string) => {
    const result = await callGmailApi('modifyMessage', { messageId, addLabelIds: ['SPAM'], removeLabelIds: ['INBOX'] });
    updateCache(prev => ({ messages: prev.messages.filter(m => m.id !== messageId) }));
    return result;
  }, []);

  const starMessage = useCallback(async (messageId: string) => {
    const result = await callGmailApi('modifyMessage', { messageId, addLabelIds: ['STARRED'] });
    updateCache(prev => ({
      messages: prev.messages.map(m => m.id === messageId ? { ...m, labelIds: [...m.labelIds, 'STARRED'] } : m),
    }));
    return result;
  }, []);

  const unstarMessage = useCallback(async (messageId: string) => {
    const result = await callGmailApi('modifyMessage', { messageId, removeLabelIds: ['STARRED'] });
    updateCache(prev => ({
      messages: prev.messages.map(m => m.id === messageId ? { ...m, labelIds: m.labelIds.filter(l => l !== 'STARRED') } : m),
    }));
    return result;
  }, []);

  // Attachment
  const getAttachment = useCallback(async (messageId: string, attachmentId: string, fromEmail?: string) => {
    return await callGmailApi('getAttachment', { messageId, attachmentId, fromEmail });
  }, []);

  const downloadAttachment = useCallback(async (messageId: string, attachmentId: string, filename: string, _mimeType?: string, fromEmail?: string) => {
    try {
      const result = await getAttachment(messageId, attachmentId, fromEmail);
      if (!result.data) throw new Error('Keine Daten erhalten');
      
      const binaryString = atob(result.data.replace(/-/g, '+').replace(/_/g, '/'));
      const byteNumbers = new Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteNumbers[i] = binaryString.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Download attachment failed:', err);
      toast.error('Download fehlgeschlagen: ' + (err.message || 'Unbekannter Fehler'));
    }
  }, [getAttachment]);

  const openAttachment = useCallback(async (messageId: string, attachmentId: string, mimeType: string, fromEmail?: string) => {
    try {
      const result = await getAttachment(messageId, attachmentId, fromEmail);
      if (!result.data) throw new Error('Keine Daten erhalten');
      
      const binaryString = atob(result.data.replace(/-/g, '+').replace(/_/g, '/'));
      const byteNumbers = new Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteNumbers[i] = binaryString.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      console.error('Open attachment failed:', err);
    }
  }, [getAttachment]);

  // Manueller Refresh
  const refresh = useCallback(async () => {
    initialized = false; // Cache invalidieren
    fetchInProgress = false;
    await fetchMessages(cache.currentLabel, searchQueryRef.current);
  }, [fetchMessages]);

  const setPollingInterval = useCallback((interval: number) => {
    setPollingIntervalState(interval);
    const settings = getGmailSettings();
    settings.pollingInterval = interval;
    localStorage.setItem('gmail_settings', JSON.stringify(settings));
  }, []);

  // Legacy-Kompatibilität
  const migrateAllEmails = useCallback(async () => {
    await runInitialSyncInternal();
  }, []);

  const archiveMessages = useCallback(async (messageIds: string[]) => {
    for (const id of messageIds) {
      await archiveMessage(id);
    }
  }, [archiveMessage]);

  return {
    // State (aus Cache)
    isConnected: state.isConnected,
    connectedEmail: state.connectedEmail,
    needsReconnect: state.needsReconnect,
    loading: state.loading,
    messages: state.messages,
    unreadCount: state.unreadCount,
    currentLabel: state.currentLabel,
    loadingMessages: state.loadingMessages,
    lastError: state.lastError,
    isPolling: state.isPolling,
    pollingInterval,
    lastSyncTime: state.lastSyncTime,
    loadProgress: state.loadProgress,
    // Infinite Scroll
    hasMore: state.hasMore,
    totalCount: state.totalCount,
    loadMoreMessages,
    // Actions
    connect,
    fetchMessages,
    getMessage,
    sendEmail,
    markAsRead,
    markAsUnread,
    trashMessage,
    archiveMessage,
    reportSpam,
    starMessage,
    unstarMessage,
    checkConnection,
    refresh,
    setPollingInterval,
    getAttachment,
    downloadAttachment,
    openAttachment,
    migrateAllEmails,
    archiveMessages,
    getThread,
  };
}
